import {useRef, useState, type FormEvent} from "react";
import createClient from "openapi-fetch";
import {useTranslation} from "react-i18next";
import type {components, paths} from "../../../../api/generated/schema";
import {ApiError, OperationKey, retryNetwork, type CaptchaResponse} from "../../../shared/api/client";
import {CaptchaChallenge, captchaChallengeFromError, type CaptchaChallengeDescriptor} from "./captcha-challenge";
import {supportPhoneChangeEnabled} from "../../../shared/config/runtime";

const client = createClient<paths>({
  baseUrl: new URL("/api/v1", window.location.origin).href.replace(/\/$/, ""),
  credentials: "same-origin",
  fetch: (request) => fetch(request),
});
type ErrorEnvelope = components["schemas"]["Error"];

export function PhoneRecovery() {
  const enabled = supportPhoneChangeEnabled();
  const {t} = useTranslation();
  const [operationId, setOperationId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string>();
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallengeDescriptor | null>(null);
  const [captchaResponse, setCaptchaResponse] = useState<CaptchaResponse | null>(null);
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const attempt = useRef(new OperationKey());
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!enabled || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(operationId) || !/^[0-9]{6}$/.test(code) || (captchaChallenge && !captchaResponse)) return;
    setBusy(true); setError(undefined);
    const submittedCaptcha = Boolean(captchaResponse);
    const body = {operation_id: operationId, code, ...(captchaResponse ? {captcha: captchaResponse} : {})};
    try {
      await retryNetwork(async (key) => {
        const {error: responseError, response} = await client.POST("/auth/phone-recovery/confirm", {
          body, params: {header: {"Idempotency-Key": key}},
        });
        if (response.status !== 204) throw new ApiError(response.status, responseError as ErrorEnvelope | undefined);
      }, attempt.current, body);
      setCode(""); setOperationId(""); setComplete(true);
    } catch (caught) {
      const challenge = caught instanceof ApiError ? captchaChallengeFromError(caught) : null;
      if (challenge) { setCaptchaChallenge(challenge); setError(t("phoneRecovery.captchaRequired")); }
      else {
        setCode("");
        setError(caught instanceof ApiError && caught.status === 429 ? t("phoneRecovery.rateLimited") :
          caught instanceof ApiError && caught.status === 503 ? t("phoneRecovery.unavailable") : t("phoneRecovery.invalid"));
      }
    } finally {
      if (submittedCaptcha) { setCaptchaResponse(null); setCaptchaAttempt((value) => value + 1); }
      setBusy(false);
    }
  };
  return <div className="standalone-auth"><a className="brand-link" href="/">{t("common.brand")}</a><section className="auth-card phone-recovery-card">
    <h1>{t("phoneRecovery.title")}</h1>
    {!enabled ? <p>{t("phoneRecovery.unavailable")}</p> : complete ? <div role="status" aria-live="polite"><p>{t("phoneRecovery.completed")}</p><a href="/signin">{t("phoneRecovery.signIn")}</a></div> : <>
      <p>{t("phoneRecovery.intro")}</p>
      <form onSubmit={(event) => void submit(event)} autoComplete="off">
        <label>{t("phoneRecovery.operationId")}<input value={operationId} onChange={(event) => setOperationId(event.target.value.trim().toLowerCase())} type="text" autoComplete="off" maxLength={36} required /></label>
        <label>{t("phoneRecovery.code")}<input value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} type="text" inputMode="numeric" pattern="[0-9]{6}" autoComplete="one-time-code" maxLength={6} required /></label>
        {captchaChallenge && <CaptchaChallenge challenge={captchaChallenge} attempt={captchaAttempt} onResponse={setCaptchaResponse} />}
        {error && <p role="alert" aria-live="assertive">{error}</p>}
        <button className="button primary" type="submit" disabled={busy || Boolean(captchaChallenge && !captchaResponse)}>{busy ? t("phoneRecovery.checking") : t("phoneRecovery.confirm")}</button>
      </form>
    </>}
  </section></div>;
}
